import type { Entity } from '@backstage/catalog-model';
import type {
  BackstageTypography,
  BaseThemeOptionsInput,
  PageTheme,
} from '@backstage/theme';
import type { Theme } from '@mui/material';
import type { PaletteOptions } from '@mui/material/styles/createPalette';
import type { TypographyStyle } from '@mui/material/styles/createTypography';
import type { CSSObject } from 'tss-react';

import {
  isApiEntity,
  isComponentEntity,
  isResourceEntity,
  stringifyEntityRef,
} from '@backstage/catalog-model';
import {
  createBaseThemeOptions,
  createUnifiedTheme,
  themes,
} from '@backstage/theme';
import {
  isBusinessDomainEntity,
  isDataProductEntity,
  isOrganisationalDomainEntity,
  isOrganisationDsoEntity,
  isOrganisationEntity,
  isPartnerSystemEntity,
  isProductEntity,
  isSolutionEntity,
  isSolutionModuleEntity,
  isTribeEntity,
} from '@ipen-portal/ipen-entity-model';
import {
  isDummyDomainEntity,
  isEnergyNetworkDomainEntity,
  isIPenPlatformDomainEntity,
} from '@ipen-portal/plugin-common';
import { colors } from '@ipen/components';
import { alpha } from '@mui/material';
import { createTheme as muiCreateTheme } from '@mui/material/styles';

const lightThemeV5 = themes.light.getTheme('v5');

export const commonThemeProperties = {
  colors: [],
  fontColor: colors.eonNeutral.color900,
  shape: '',
};

const commonRedTheme = {
  ...commonThemeProperties,
  backgroundImage: 'url(/backgrounds/red.svg)',
};

const commonTurquoiseTheme = {
  ...commonThemeProperties,
  backgroundImage: 'url(/backgrounds/blue.svg)',
};

const commonYellowTheme = {
  ...commonThemeProperties,
  backgroundImage: 'url(/backgrounds/yellow.svg)',
};

const commonPurpleTheme = {
  ...commonThemeProperties,
  backgroundImage: 'url(/backgrounds/purple.svg)',
};

export type IPenThemes = keyof ReturnType<typeof createIpenPageThemes>;

export type IPenTypographyAdditions = BackstageTypography & {
  /**
   * e.g. buttons
   */
  button: TypographyStyle;
  h1: TypographyStyle;
  h2: TypographyStyle;
  h3: TypographyStyle;
  h4: TypographyStyle;
  h5: TypographyStyle;

  h6: TypographyStyle;
  /**
   * We use ipenSubTitle instead of subtitle, b/c subtitle1 and subtitle2 are used by standard backstage components and
   * have completely different font sizes
   */
  ipenSubTitle1: TypographyStyle;
  ipenSubTitle2: TypographyStyle;
  ipenSubTitle3: TypographyStyle;
  ipenSubTitle4: TypographyStyle;
  /**
   * links
   */
  link: TypographyStyle;

  /**
   * normal continuous text
   */
  text1: TypographyStyle;

  /**
   * e.g. text in panel
   */
  text2: TypographyStyle;

  /**
   * e.g. smaller continuous text
   */
  text3: TypographyStyle;

  /**
   * e.g. very small continuous text
   */
  text4: TypographyStyle;
};

if (!lightThemeV5) {
  throw new Error('Missing theme');
}

export const themeOptions: BaseThemeOptionsInput<PaletteOptions> = {
  defaultPageTheme: 'default',
  fontFamily: 'EONBrixSans',
  pageTheme: {
    default: commonPurpleTheme,
    purple: commonPurpleTheme,
    red: commonRedTheme,
    turquoise: commonTurquoiseTheme,
    yellow: commonYellowTheme,
  },
  palette: {
    ...lightThemeV5.palette,

    action: {
      disabledBackground: 'none',
    },
    background: {
      default: colors.eonNeutral.color100,
      paper: '#FFFFFF',
      switch: colors.eonNeutral.color300,
    },
    banner: {
      error: colors.ipenRed.color100,
      info: colors.eonNeutral.color100,
      link: colors.eonTurquoise.color700,
      text: colors.eonNeutral.color900,
    },
    border: colors.eonNeutral.color100,
    divider: '#FFFFFF',
    eonNeutral: colors.eonNeutral,
    eonRed: colors.eonRed,
    eonSkin: colors.eonSkin,

    eonTurquoise: colors.eonTurquoise,
    eonYellow: colors.eonYellow,
    error: {
      main: colors.ipenRed.color200,
    },
    errorBackground: colors.eonRed.color100,
    errorText: colors.eonRed.color900,
    grey: {
      100: colors.eonNeutral.color100,
      200: colors.eonNeutral.color200,
      300: colors.eonNeutral.color300,
      400: colors.eonNeutral.color400,
      500: colors.eonNeutral.color500,
      600: colors.eonNeutral.color600,
      700: colors.eonNeutral.color700,
      800: colors.eonNeutral.color800,
      900: colors.eonNeutral.color900,
    },
    info: {
      main: colors.eonNeutral.color700,
    },
    infoBackground: colors.eonTurquoise.color100,
    infoText: colors.eonTurquoise.color900,
    ipenGreen: colors.ipenGreen,
    ipenOrange: colors.ipenOrange,
    ipenPurple: colors.ipenPurple,
    navigation: {
      background: colors.eonNeutral.color100,
      color: colors.eonNeutral.color900,
      indicator: colors.eonRed.color500,
      navItem: {
        hoverBackground: colors.eonNeutral.color200,
      },
      selectedColor: colors.eonNeutral.color900,
      submenu: {
        background: colors.eonNeutral.color100,
      },
    },
    primary: {
      ...colors.purple,
    },
    secondary: {
      ...colors.neutral,
    },
    success: {
      main: colors.ipenGreen.color200,
    },
    warning: {
      main: colors.ipenOrange.color200,
    },
    warningBackground: colors.ipenOrange.color100,
    warningText: colors.eonTurquoise.color900,
  },
};

export const ipenPageThemes = Object.keys(
  themeOptions.pageTheme as Record<IPenThemes, PageTheme>,
) as IPenThemes[];

const createThemeWithOverrides = (
  theme: Theme,
  pageThemes: Record<string, PageTheme>,
  typographyVariants: IPenTypographyAdditions,
) =>
  createUnifiedTheme({
    ...theme,
    components: {
      BackstageHeaderTabs: {
        styleOverrides: {
          defaultTab: {
            fontSize: theme.typography.body2.fontSize,
            marginTop: theme.spacing(2),
            padding: '0px 16px',
          },
        },
      },
      BackstageItemCardGrid: {
        styleOverrides: {
          root: {
            gridAutoRows: 'auto',
            paddingLeft: '9px',
            width: '99%',
          },
        },
      },
      BackstageItemCardHeader: {
        styleOverrides: {
          root: {
            color: 'black',
            marginBottom: '10px',
          },
        },
      },
      BackstagePage: {
        styleOverrides: {
          root: {
            /*
                I disabled the next line b/c otherwise the template editor (http://localhost:3000/create/edit -> click the
                1st Tile "Edit Template Form") does not render correctly, so if one must enable this property again, please
                check that the editor works as well!
                */
            // height: 'unset',
            height: '100%',
          },
        },
      },
      BackstageSidebar: {
        styleOverrides: {
          drawer: {
            top: 80,
          },
        },
      },
      BackstageTable: {
        styleOverrides: {
          root: {
            '& ::-webkit-scrollbar-thumb': {
              backgroundColor: alpha(colors.eonNeutral.color900, 0.2),
              border: '1px solid #7f7f7f',
              borderRadius: 2,
            },
            '& ::-webkit-scrollbar-track': {
              borderRadius: 2,
              boxShadow: 'inset 0 0 6px rgba(0, 0, 0, 0.2)',
            },
            '& ::-webkit-scrollbar:horizontal': {
              height: 7,
            },
            '& ::-webkit-scrollbar:vertical': {
              width: 7,
            },
          },
        },
      },
      BreadcrumbsCurrentPage: {
        styleOverrides: {
          root: {
            fontStyle: 'unset',
          },
        },
      },

      MuiBreadcrumbs: {
        styleOverrides: {
          root: {
            '& li a': {
              color: colors.eonNeutral.color100,
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          containedPrimary: {
            '&.Mui-disabled': {
              color: theme.palette.primary.contrastText,
            },
          },
          containedSecondary: {
            '&.Mui-disabled': {
              color: theme.palette.secondary.contrastText,
            },
            '&:hover': {
              backgroundColor: theme.palette.secondary.dark,
              color: theme.palette.secondary.contrastText,
            },
          },
          outlinedPrimary: {
            '&:hover': {
              backgroundColor: theme.palette.primary.main,
              borderColor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
            },
            'color': theme.palette.primary.contrastText,
          },
          root: {
            '&$disabled': {
              opacity: 0.7,
            },
            '&.Mui-disabled': {
              color: theme.palette.primary.main,
            },
            'fontWeight': 'bold',
          },
          textSecondary: {
            '&:hover': {
              backgroundColor: colors.eonTurquoise.color700,
            },
            'backgroundColor': theme.palette.secondary.contrastText,
          },
        },
      },
      MuiCardContent: {
        styleOverrides: {
          root: {
            padding: '1.5rem',
            paddingTop: 0,
          },
        },
      },
      MuiCardHeader: {
        styleOverrides: {
          root: {
            padding: '1.5rem !important',
            paddingBottom: '0 !important',
          },
          subheader: {
            padding: 0,
          },
        },
      },
      MuiCheckbox: {
        styleOverrides: {
          colorPrimary: {
            '&.Mui-disabled': {
              color: colors.eonNeutral.color700,
            },
          },
          colorSecondary: {
            '&.Mui-checked': {
              color: colors.eonNeutral.color800,
            },
            '&.Mui-disabled': {
              color: theme.palette.primary.contrastText,
            },
          },
          root: {
            color: colors.eonNeutral.color800,
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          deleteIcon: {
            color: colors.eonNeutral.color100,
          },
          deleteIconColorPrimary: {
            color: colors.eonNeutral.color100,
          },
          outlined: {
            '&:hover': {
              background: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
            },
          },
          root: {
            margin: '4px',
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          colorSecondary: {
            color: theme.palette.secondary.main,
          },
          root: {
            '&.Mui-focused': {
              color: theme.palette.secondary.dark,
            },
          },
        },
      },
      MuiGrid: {
        styleOverrides: {
          root: {
            '& .masonry': {
              padding: '0 1.5rem',
              [theme.breakpoints.down('xs')]: {
                padding: '0',
              },
            },
          },
        },
      },
      MuiListItem: {
        styleOverrides: {
          button: {
            color: theme.palette.primary.dark,
          },
          root: {
            // we need to increase the specificity of the list-item hover to be higher than the one of the link, so that the
            // nav does not have underlines
            '&:not(#dummy):hover': {
              textDecoration: 'none',
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '&$focused': {
              '&fieldset': {
                borderColor: `${theme.palette.secondary.contrastText} !important`,
              },
            },
            '&.Mui-focused fieldset': {
              borderColor: `${theme.palette.secondary.dark} !important`,
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          rounded: {
            borderRadius: 10,
          },
        },
      },
      MuiRadio: {
        styleOverrides: {
          colorPrimary: {
            '&.Mui-checked': {
              color: theme.palette.secondary.contrastText,
            },
            '&.Mui-disabled': {
              color: colors.eonNeutral.color700,
            },
          },
          colorSecondary: {
            '&.Mui-disabled': {
              color: theme.palette.primary.contrastText,
            },
            'color': theme.palette.primary.contrastText,
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            '&:focus': {
              backgroundColor: theme.palette.background.default,
            },
          },
        },
      },
      MuiStepLabel: {
        styleOverrides: {
          iconContainer: {
            '& > [class*="completed"]': {
              color: theme.palette.success.main,
            },
            '& > [class*="error"]': {
              color: theme.palette.error.main,
            },
            'display': 'table',
            'margin': 'auto',
            'textAlign': 'center',
          },
          label: {
            textAlign: 'center',
          },
          root: {
            display: 'block',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBlockColor: colors.eonNeutral.color600,
          },
        },
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            borderColor: colors.eonNeutral.color600,
            borderRadius: '4px',
            borderStyle: 'solid',
            borderWidth: 1,
          },
        },
      },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            '&.Mui-selected': {
              borderColor: 'rgba(0, 0, 0, 0.12)',
            },
            'border': '1px solid rgba(0, 0, 0, 0.12)',
          },
        },
      },
      MuiTypography: {
        styleOverrides: {
          body1: typographyVariants.text1 as CSSObject,
          body2: typographyVariants.text2 as CSSObject,
          button: typographyVariants.button as CSSObject,
          h1: typographyVariants.h1 as CSSObject,
          h2: typographyVariants.h2 as CSSObject,
          h3: typographyVariants.h3 as CSSObject,
          h4: typographyVariants.h4 as CSSObject,
          h5: typographyVariants.h5 as CSSObject,
          h6: typographyVariants.h6 as CSSObject,
          // colorTextSecondary: {
          //   color: eonNeutral.color700,
          // },
        },
      },
    },
    defaultPageTheme: 'default',
    pageTheme: pageThemes,
    typography: typographyVariants,
  });

const createTypographyVariants = (): IPenTypographyAdditions => {
  if (!lightThemeV5) {
    throw new Error('Missing theme');
  }
  return {
    button: {
      fontSize: 14,
      fontWeight: 'bold',
      [lightThemeV5.breakpoints.down('sm')]: {
        fontSize: 16,
        lineHeight: 1.125,
      },
      lineHeight: 1.333_333_333,
      textTransform: 'uppercase',
    },
    fontFamily: 'EONBrixSans',
    h1: {
      fontSize: 60,
      fontWeight: 800,
      [lightThemeV5.breakpoints.down('sm')]: {
        fontSize: 36,
      },
      lineHeight: 1,
      marginBottom: 10,
    },
    h2: {
      fontSize: 46,
      fontWeight: 800,
      [lightThemeV5.breakpoints.down('sm')]: {
        fontSize: 32,
      },
      lineHeight: 1,
      marginBottom: 8,
    },
    h3: {
      fontSize: 24,
      fontWeight: 800,
      [lightThemeV5.breakpoints.down('sm')]: {
        fontSize: 24,
      },
      lineHeight: 1,
      marginBottom: 6,
    },
    h4: {
      fontSize: 20,
      fontWeight: 700,
      [lightThemeV5.breakpoints.down('sm')]: {
        fontSize: 20,
      },
      lineHeight: 1,
      marginBottom: 6,
    },
    h5: {
      fontSize: 18,
      fontWeight: 800,
      lineHeight: 1,
      marginBottom: 4,
    },
    h6: {
      fontSize: 16,
      fontWeight: 800,
      lineHeight: 1,
      marginBottom: 2,
    },
    htmlFontSize: 16,
    ipenSubTitle1: {
      fontSize: 24,
      fontWeight: 800,
      [lightThemeV5.breakpoints.down('sm')]: {
        lineHeight: 1.2,
      },
      lineHeight: 1.333_333_333,
    },
    ipenSubTitle2: {
      fontSize: 20,
      fontWeight: 800,
      lineHeight: 1.25,
    },
    ipenSubTitle3: {
      fontSize: 18,
      fontWeight: 800,
      lineHeight: 1.333_333_333,
    },
    ipenSubTitle4: {
      fontSize: 16,
      fontWeight: 800,
      [lightThemeV5.breakpoints.down('sm')]: {
        lineHeight: 1.125,
      },
      lineHeight: 1.5,
    },
    link: {
      '&:active': {
        color: colors.eonNeutral.color800,
      },
      '&:hover': {
        color: colors.eonNeutral.color800,
      },
      '&:visited': {
        color: colors.eonRed.color900,
      },
      'color': colors.eonRed.color400,
      'fontSize': 18,
      [lightThemeV5.breakpoints.down('sm')]: {
        fontSize: '1.25rem',
        lineHeight: 1.333_333_333,
      },
      'lineHeight': 1.333_333_333,
    },
    text1: {
      color: colors.eonNeutral.color800,
      fontSize: 18,
      [lightThemeV5.breakpoints.down('sm')]: {
        fontSize: 20,
        lineHeight: 1.125,
      },
      lineHeight: 1.333_333_333,
    },
    text2: {
      color: colors.eonNeutral.color800,
      fontSize: 16,
      [lightThemeV5.breakpoints.down('sm')]: {
        fontSize: 18,
      },
      lineHeight: 1.43,
    },
    text3: {
      color: colors.eonNeutral.color800,
      fontSize: 14,
      [lightThemeV5.breakpoints.down('sm')]: {
        fontSize: 16,
        lineHeight: 1.125,
      },
      lineHeight: 1.142_857_143,
    },
    text4: {
      color: colors.eonNeutral.color800,
      fontSize: 12,
      lineHeight: 1.333_333_333,
    },
  };
};

const createUnifiedPageTheme = (
  paletteOverrides: PaletteOptions,
  pageThemes: Record<string, PageTheme>,
  typographyVariants: IPenTypographyAdditions,
) => {
  const customizedBaseTheme = createBaseThemeOptions({
    ...themeOptions,
    palette: {
      ...themeOptions.palette,
      ...paletteOverrides,
      error: {
        ...themeOptions.palette.error,
        contrastText: colors.eonNeutral.color100,
        dark: colors.ipenRed.color300,
        light: colors.ipenRed.color100,
        main: colors.ipenRed.color200,
        ...paletteOverrides.error,
      },
      info: {
        ...themeOptions.palette.info,
        contrastText: colors.eonNeutral.color100,
        dark: colors.ipenPurple.color400,
        light: colors.ipenPurple.color100,
        main: colors.ipenPurple.color300,
        ...paletteOverrides.info,
      },
      primary: {
        ...themeOptions.palette.primary,
        ...paletteOverrides.primary,
      },
      secondary: {
        ...themeOptions.palette.secondary,
        ...paletteOverrides.secondary,
      },
      success: {
        ...themeOptions.palette.success,
        contrastText: colors.eonNeutral.color100,
        dark: colors.ipenGreen.color300,
        light: colors.ipenGreen.color100,
        main: colors.ipenGreen.color200,
        ...paletteOverrides.success,
      },
      warning: {
        ...themeOptions.palette.warning,
        contrastText: colors.eonNeutral.color100,
        dark: colors.ipenOrange.color300,
        light: colors.ipenOrange.color100,
        main: colors.ipenOrange.color200,
        ...paletteOverrides.warning,
      },
    },
  });

  const pageTheme = muiCreateTheme({
    ...customizedBaseTheme,
    typography: {
      ...customizedBaseTheme.typography,
      ...typographyVariants,
    },
  });
  return createThemeWithOverrides(pageTheme, pageThemes, typographyVariants);
};

const predicateThemeMapper: readonly [IPenThemes, ...readonly ((e: Entity) => boolean)[]][] = [
  [
    'turquoise',
    isIPenPlatformDomainEntity,
    isPartnerSystemEntity,
    isProductEntity,
    isDataProductEntity,
  ],
  [
    'yellow',
    isSolutionEntity,
    isSolutionModuleEntity,
    isEnergyNetworkDomainEntity,
    isOrganisationalDomainEntity,
    isBusinessDomainEntity,
    isDummyDomainEntity,
    e => stringifyEntityRef(e) === 'group:default/dam-team',
  ],
  ['purple', isTribeEntity],
  ['red', isOrganisationEntity, isOrganisationDsoEntity],

  // fallback by kind
  ['turquoise', isComponentEntity, isResourceEntity, isApiEntity],
];

export const getThemeOfEntity = (
  entity: Entity | null | undefined,
): IPenThemes => {
  if (!entity) {
    return 'default';
  }
  return (
    predicateThemeMapper.find(([_, ...predicates]) =>
      predicates.some(predicate => predicate(entity)),
    )?.[0] ?? 'default'
  );
};

const createIpenPageThemes = (
  typographyVariants: IPenTypographyAdditions,
  pageThemes: Record<string, PageTheme>,
) => ({
  default: createUnifiedPageTheme(
    { primary: colors.purple, secondary: colors.yellow },
    pageThemes,
    typographyVariants,
  ),
  purple: createUnifiedPageTheme(
    { primary: colors.purple, secondary: colors.yellow },
    pageThemes,
    typographyVariants,
  ),
  red: createUnifiedPageTheme(
    { primary: colors.red, secondary: colors.turquoise },
    pageThemes,
    typographyVariants,
  ),
  turquoise: createUnifiedPageTheme(
    { primary: colors.turquoise, secondary: colors.yellow },
    pageThemes,
    typographyVariants,
  ),
  yellow: createUnifiedPageTheme(
    { primary: colors.yellow, secondary: colors.turquoise },
    pageThemes,
    typographyVariants,
  ),
});

const createTheme = (options: BaseThemeOptionsInput<PaletteOptions>) => {
  const typographyVariants = createTypographyVariants();
  const backstageThemeOptions = createBaseThemeOptions(options);
  const theme = muiCreateTheme({
    ...backstageThemeOptions,
    typography: {
      ...backstageThemeOptions.typography,
      ...typographyVariants,
    },
  });
  const pageThemes = createIpenPageThemes(
    typographyVariants,
    options.pageTheme ?? {},
  );
  // These are the overrides that Backstage applies to `material- ui` components

  const unifiedTheme = createThemeWithOverrides(
    theme,
    options.pageTheme ?? {},
    typographyVariants,
  );
  const getIpenThemeOfPage = (themeId: IPenThemes) =>
    pageThemes[themeId] ?? theme;

  // Add getIpenThemeOfPage to both unified theme and v5 theme
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  unifiedTheme.getIpenThemeOfPage = getIpenThemeOfPage;
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  unifiedTheme.getTheme('v5').getIpenThemeOfPage = getIpenThemeOfPage;

  return unifiedTheme;
};

export const customTheme = createTheme(themeOptions);
